import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { DatePicker } from "@/components/ui/DatePicker";
import { Select } from "@/components/ui/Select";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useAuthStore } from "@/store/auth";
import { useLaborCostCheck } from "../hooks/useB2BInvoices";
import { LaborCostWarning } from "./LaborCostWarning";
import { MIN_INVOICE_PERIOD, MONTH_NAMES } from "../types";
import type { B2BInvoice, B2BInvoiceFormData } from "../types";

const MONTH_NAMES_GENITIVE = [
  "stycznia",
  "lutego",
  "marca",
  "kwietnia",
  "maja",
  "czerwca",
  "lipca",
  "sierpnia",
  "września",
  "października",
  "listopada",
  "grudnia",
];

/** Generuje opcje dropdownu „okres" w postaci `"YYYY-MM"` od MIN_INVOICE_PERIOD do bieżącego miesiąca. */
function buildPeriodOptions(): { value: string; label: string }[] {
  const today = new Date();
  const endY = today.getFullYear();
  const endM = today.getMonth() + 1;
  const opts: { value: string; label: string }[] = [];
  let y: number = MIN_INVOICE_PERIOD.year;
  let m: number = MIN_INVOICE_PERIOD.month;
  while (y < endY || (y === endY && m <= endM)) {
    opts.push({
      value: `${y}-${String(m).padStart(2, "0")}`,
      label: `${MONTH_NAMES[m - 1]} ${y}`,
    });
    if (m === 12) {
      m = 1;
      y += 1;
    } else {
      m += 1;
    }
  }
  // Odwracamy — najnowszy okres na górze, użytkownik najczęściej wystawia za poprzedni miesiąc.
  return opts.reverse();
}

const schema = z.object({
  period: z
    .string()
    .regex(/^\d{4}-\d{2}$/, "Wybierz okres rozliczeniowy"),
  invoice_number: z.string().min(1, "Numer faktury jest wymagany"),
  issue_date: z.string().min(1, "Data wystawienia jest wymagana"),
  amount_gross: z
    .string()
    .min(1, "Kwota brutto jest wymagana")
    .refine((v) => Number(v) > 0, "Kwota musi być większa od zera"),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Props {
  invoice?: B2BInvoice;
  /** `"YYYY-MM"` preselected for a new invoice (e.g. an overdue month from the list). */
  initialPeriod?: string;
  /** New invoice: the only periods on offer (`"YYYY-MM"`, months with a B2B day per
   *  the contract history). Empty/absent → every month since MIN_INVOICE_PERIOD. */
  allowedPeriods?: string[];
  onSubmit: (data: B2BInvoiceFormData) => Promise<void> | void;
  isSubmitting: boolean;
  submitLabel?: string;
}

/** Mała gwiazdka przy etykiecie pola wymaganego. */
function Required() {
  return <span className="ml-0.5 text-rose-600">*</span>;
}

export function B2BInvoiceForm({
  invoice,
  initialPeriod: requestedPeriod,
  allowedPeriods,
  onSubmit,
  isSubmitting,
  submitLabel = "Zapisz szkic",
}: Props) {
  const user = useAuthStore((s) => s.user);
  const isEdit = !!invoice;

  const periodOptions = useMemo(() => {
    const all = buildPeriodOptions();
    if (!allowedPeriods?.length) return all;
    const allowed = new Set(allowedPeriods);
    return all.filter((o) => allowed.has(o.value));
  }, [allowedPeriods]);

  const initialPeriod = invoice
    ? `${invoice.period_year}-${String(invoice.period_month).padStart(2, "0")}`
    : periodOptions.find((o) => o.value === requestedPeriod)?.value ??
      periodOptions[0]?.value ??
      "";

  const defaults: FormValues = {
    period: initialPeriod,
    invoice_number: invoice?.invoice_number ?? "",
    issue_date: invoice?.issue_date ?? new Date().toISOString().slice(0, 10),
    amount_gross: invoice?.amount_gross ?? "",
    notes: invoice?.notes ?? "",
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
  });

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Live labor-cost-check (debounced)
  const period = watch("period");
  const [year, month] = period.split("-").map(Number);
  const amountStr = watch("amount_gross");
  const [debouncedAmount, setDebouncedAmount] = useState(amountStr);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedAmount(amountStr), 400);
    return () => clearTimeout(t);
  }, [amountStr]);

  const checkParams =
    Number.isFinite(year) && Number.isFinite(month) && year >= MIN_INVOICE_PERIOD.year
      ? {
          year,
          month,
          amount_gross:
            debouncedAmount && Number(debouncedAmount) > 0 ? debouncedAmount : undefined,
        }
      : null;
  const { data: warning, isFetching } = useLaborCostCheck(checkParams);

  // Stawka VAT pracownika — pobierana z auth store (read-only).
  const vatRate = user?.vat_rate ?? null;
  const hasVatRate = vatRate !== null && vatRate !== undefined;
  const vatLabel = hasVatRate
    ? vatRate === "zw"
      ? "Zwolniony"
      : `${vatRate}%`
    : null;

  // Pracownik bez ustawionej stawki VAT nie może utworzyć nowej faktury (twarda blokada).
  // Edycja istniejącej faktury (gdy CEO później cofnął stawkę) pozostaje możliwa.
  const blockedByMissingVat = !isEdit && !hasVatRate;

  // Skalkulowane "Kwota netto" na podstawie stawki VAT (Zwolniony i 0% → netto = brutto).
  const grossNumber = Number(amountStr);
  const vatPct = (() => {
    if (!hasVatRate) return null;
    if (vatRate === "zw") return 0;
    return Number(vatRate);
  })();
  const netNumber =
    Number.isFinite(grossNumber) && grossNumber > 0 && vatPct !== null
      ? grossNumber / (1 + vatPct / 100)
      : null;
  const netDisplay = netNumber !== null
    ? new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: "PLN",
        minimumFractionDigits: 2,
        // "always" forces a thousands separator for 4-digit pl-PL values; the pl-PL
        // CLDR default (minimumGroupingDigits=2) otherwise skips grouping below 10 000.
        useGrouping: "always",
      }).format(netNumber)
    : "—";

  const submit = handleSubmit(async (values) => {
    if (blockedByMissingVat) return;

    // Walidacja pliku — wymagany przy nowej fakturze, opcjonalny przy edycji
    if (!isEdit && !file) {
      setFileError("Plik PDF faktury jest wymagany.");
      return;
    }
    setFileError(null);

    const [py, pm] = values.period.split("-").map(Number);
    const payload: B2BInvoiceFormData = {
      period_year: py,
      period_month: pm,
      invoice_number: values.invoice_number,
      issue_date: values.issue_date,
      amount_gross: values.amount_gross,
      notes: values.notes ?? "",
      invoice_file: file ?? null,
    };
    await onSubmit(payload);
  });

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Sekcja stawki VAT — read-only; gdy brak → czerwona blokada */}
      {blockedByMissingVat ? (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
          <div className="font-medium">
            Twoja stawka VAT nie jest ustawiona w systemie.
          </div>
          <p className="mt-1 text-xs text-rose-700">
            Nie możesz wystawić ani zapisać szkicu faktury B2B, dopóki CEO nie ustawi
            Twojej stawki VAT (panel CEO → Role użytkowników → kolumna „VAT"). Skontaktuj
            się z zarządem, aby odblokować wystawianie faktur.
          </p>
          <p className="mt-2 text-xs text-rose-700">
            <strong>Uwaga na różnicę:</strong>{" "}
            <em>brak</em> oznacza „Twoja firma nie jest VATowcem" (CEO nie nadał Ci stawki) —
            wystawianie faktur jest zablokowane.{" "}
            <em>0%</em> to prawidłowa stawka VAT (np. eksport, WDT) dla VATowca —
            ustawia ją CEO i wystawianie faktur jest możliwe.
          </p>
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          <div className="flex items-center justify-between gap-2">
            <div>
              <span className="text-slate-500">Twoja stawka VAT:</span>{" "}
              <strong className="text-slate-800">{vatLabel}</strong>{" "}
              <span className="text-xs text-slate-500">
                (ustawia CEO; zmiana wymaga kontaktu z zarządem)
              </span>
            </div>
            <span
              className="text-slate-400 hover:text-slate-600 cursor-help"
              title={
                "VAT 0% — prawidłowa stawka dla VATowca (np. eksport, WDT). " +
                "Zwolniony — firma jest zwolniona z VAT (np. przy obrocie poniżej limitu). " +
                "Brak (null) — CEO jeszcze nie ustawił stawki, wystawianie faktur zablokowane. " +
                "0% i Zwolniony są różne księgowo: 0% to VAT należny (po prostu zerowy), " +
                "a Zwolniony oznacza że firma w ogóle nie jest płatnikiem VAT."
              }
            >
              <Info size={14} />
            </span>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-slate-700">
          Okres rozliczeniowy<Required />
        </label>
        <Select
          {...register("period")}
          options={periodOptions}
        />
        {errors.period && (
          <p className="text-xs text-rose-600 mt-1">{errors.period.message}</p>
        )}
        <p className="mt-1 text-xs text-slate-500">
          Dostępne miesiące: od {MONTH_NAMES_GENITIVE[MIN_INVOICE_PERIOD.month - 1]}{" "}
          {MIN_INVOICE_PERIOD.year}.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Numer faktury<Required />
          </label>
          <Input {...register("invoice_number")} placeholder="np. FV/05/2026" />
          {errors.invoice_number && (
            <p className="text-xs text-rose-600 mt-1">{errors.invoice_number.message}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Data wystawienia<Required />
          </label>
          <Controller
            control={control}
            name="issue_date"
            render={({ field }) => (
              <DatePicker value={field.value} onChange={field.onChange} />
            )}
          />
          {errors.issue_date && (
            <p className="text-xs text-rose-600 mt-1">{errors.issue_date.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Kwota brutto (PLN)<Required />
          </label>
          <Input
            type="number"
            step="0.01"
            min={0}
            {...register("amount_gross")}
            placeholder="0.00"
          />
          {errors.amount_gross && (
            <p className="text-xs text-rose-600 mt-1">{errors.amount_gross.message}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Kwota netto (PLN) <span className="text-xs text-slate-400 font-normal">(obliczana)</span>
          </label>
          <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {netDisplay}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {hasVatRate
              ? vatRate === "zw"
                ? "Zwolniony z VAT → netto = brutto"
                : vatPct === 0
                  ? "Stawka 0% → netto = brutto"
                  : `Brutto ÷ (1 + ${vatPct}%) = netto`
              : "Ustaw stawkę VAT, aby zobaczyć kwotę netto"}
          </p>
        </div>
      </div>

      <LaborCostWarning warning={warning} isLoading={isFetching} />

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Plik faktury (PDF){!isEdit && <Required />}
        </label>
        <FileDropZone
          files={file ? [file] : []}
          onFilesChange={(fs) => {
            setFile(fs[0] ?? null);
            setFileError(null);
          }}
          accept="application/pdf"
        />
        {fileError && (
          <p className="text-xs text-rose-600 mt-1">{fileError}</p>
        )}
        {invoice?.invoice_file && !file && (
          <p className="mt-2 text-xs text-slate-600">
            Aktualny plik:{" "}
            <span className="font-medium">{invoice.invoice_file.original_filename}</span>
            {invoice.invoice_file_url && (
              <>
                {" — "}
                <a
                  href={invoice.invoice_file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-700 underline"
                >
                  pobierz
                </a>
              </>
            )}
          </p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700">
          Uwagi (opcjonalne)
        </label>
        <textarea
          {...register("notes")}
          rows={3}
          className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          placeholder="Np. korekta wcześniejszej faktury, dodatkowe godziny itp."
        />
      </div>

      <p className="text-xs text-slate-500">
        Pola oznaczone <Required /> są wymagane.
      </p>

      <div className="flex justify-end gap-2">
        <Button
          type="submit"
          disabled={isSubmitting || blockedByMissingVat}
          title={
            blockedByMissingVat
              ? "Wystawianie faktur jest zablokowane — brak stawki VAT (CEO musi ją ustawić)"
              : undefined
          }
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
